import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

import {
  Arrow,
  Dot,
  Dots,
  Footer,
  PageLabel,
  Track,
  Viewport,
  ViewportRow,
  Wrapper,
} from "./styles";
import type { CarouselProps } from "./types";

const DEFAULT_OPTIONS = {
  align: "start" as const,
  containScroll: "trimSnaps" as const,
  dragFree: false,
};

export const Carousel: React.FC<CarouselProps> = ({
  children,
  ariaLabelPrev = "Previous",
  ariaLabelNext = "Next",
  options,
  showArrows = true,
  showDots = true,
  showPageLabel = true,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    ...DEFAULT_OPTIONS,
    ...options,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const syncState = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setSnapCount(emblaApi.scrollSnapList().length);
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }
    syncState();
    emblaApi.on("select", syncState);
    emblaApi.on("reInit", syncState);
    return () => {
      emblaApi.off("select", syncState);
      emblaApi.off("reInit", syncState);
    };
  }, [emblaApi, syncState]);

  const onPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const onNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const onDotClick = useCallback(
    (index: number) => emblaApi?.scrollTo(index),
    [emblaApi],
  );

  const hasMultipleSnaps = snapCount > 1;
  const renderArrows = showArrows && hasMultipleSnaps;
  const renderFooter = (showDots || showPageLabel) && hasMultipleSnaps;

  return (
    <Wrapper>
      <ViewportRow>
        {renderArrows && (
          <Arrow
            $side="left"
            onClick={onPrev}
            disabled={!canScrollPrev}
            aria-label={ariaLabelPrev}
          >
            <ChevronLeftRounded />
          </Arrow>
        )}
        <Viewport ref={emblaRef}>
          <Track>{children}</Track>
        </Viewport>
        {renderArrows && (
          <Arrow
            $side="right"
            onClick={onNext}
            disabled={!canScrollNext}
            aria-label={ariaLabelNext}
          >
            <ChevronRightRounded />
          </Arrow>
        )}
      </ViewportRow>
      {renderFooter && (
        <Footer>
          {showDots && (
            <Dots>
              {Array.from({ length: snapCount }).map((_, index) => (
                <Dot
                  // Pagination dots have no stable id; index is the identity.
                  key={index}
                  $active={index === selectedIndex}
                  type="button"
                  onClick={() => onDotClick(index)}
                  aria-label={`Go to page ${index + 1}`}
                  aria-current={index === selectedIndex}
                />
              ))}
            </Dots>
          )}
          {showPageLabel && (
            <PageLabel>
              {selectedIndex + 1} / {snapCount}
            </PageLabel>
          )}
        </Footer>
      )}
    </Wrapper>
  );
};
